import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Descriptions from 'antd/lib/descriptions';
import Space from 'antd/lib/space';
import Switch from 'antd/lib/switch';
import Text from 'antd/lib/typography/Text';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import { EditOutlined } from '@ant-design/icons';
import { RootState } from '@/reducers/reducers';
import { selectAppSettings } from '@/selectors/selectors';
import { AppComponentProps } from '@/typescriptHelpers';
import { IssueSourceType, SubscriptionFeatureType, TriageSettings as TriageSettingsData } from '@/types';
import { displayTextForIssueSourceType } from '@/helpers/displayHelpers';
import { useUpdateTriageSettingsMutation } from '@/middlewares/defaultApi';
import { updateAppSettingsSuccessAction } from '@/actions/appSettings';
import { globalMessage } from '@/utils/globalMessage';
import { PlanRestrictedFeatureWrapper } from '@/components/planRestrictions/PlanRestrictedFeatureWrapper';
import { useRestrictedFeatureIsDisabled } from '@/components/planRestrictions/planRestrictionHelperComponents';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { runwayColors } from '../styles/globalStyles';
import TicketFiltersSettings from '../triage/TicketFiltersSettings';

const selectableSourceTypes = Object.values(IssueSourceType).filter((type) => type !== IssueSourceType.Runway);

const sourceTypeOptions = selectableSourceTypes.map((type) => ({
    label: displayTextForIssueSourceType(type),
    value: type,
}));

const enabledSourcesFromDisabled = (disabledSources: IssueSourceType[] | undefined): IssueSourceType[] =>
    selectableSourceTypes.filter((type) => !(disabledSources ?? []).includes(type));

type OpenModal = 'signals' | 'subtasks' | null;

const TriageSettings: React.FC<AppComponentProps> = ({ orgId, appId }) => {
    const dispatch = useDispatch();
    const isPaywalled = useRestrictedFeatureIsDisabled(SubscriptionFeatureType.Triage);
    const savedSettings = useSelector(
        (state: RootState): TriageSettingsData => selectAppSettings(state, appId)?.triageSettings ?? {},
    );
    const savedEnabledSources = enabledSourcesFromDisabled(savedSettings.disabledSources);
    const savedMergeSubtasks = !savedSettings.separateIssuesForSubtasks;

    const [openModal, setOpenModal] = React.useState<OpenModal>(null);
    const [selectedSources, setSelectedSources] = React.useState<IssueSourceType[]>(savedEnabledSources);
    const [mergeSubtasks, setMergeSubtasks] = React.useState(savedMergeSubtasks);

    const [updateTriageSettings, { isLoading, data: updateResponse }] = useUpdateTriageSettingsMutation();

    const openSignalsModal = () => {
        setSelectedSources(savedEnabledSources);
        setOpenModal('signals');
    };

    const openSubtasksModal = () => {
        setMergeSubtasks(savedMergeSubtasks);
        setOpenModal('subtasks');
    };

    const saveSettings = (settings: TriageSettingsData) => {
        updateTriageSettings({ orgId, appId, settings });
    };

    const onSaveSignals = () =>
        saveSettings({
            ...savedSettings,
            disabledSources: selectableSourceTypes.filter((type) => !selectedSources.includes(type)),
        });

    const onSaveSubtasks = () => saveSettings({ ...savedSettings, separateIssuesForSubtasks: !mergeSubtasks });

    React.useEffect(() => {
        if (updateResponse?.success) {
            dispatch(updateAppSettingsSuccessAction({ appId, appSettings: updateResponse.data }));
            globalMessage.success({ content: 'Triage settings updated!', key: 'triageSettingsUpdated' });
            setOpenModal(null);
        } else if (updateResponse?.errorMessage) {
            globalMessage.error(updateResponse.errorMessage);
        }
    }, [updateResponse, appId, dispatch]);

    const renderLabel = (label: string) => (
        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Triage} requiresTooltip shouldFakeDisabledState>
            <Text style={{ color: runwayColors.darkTextGrey }}>{label}</Text>
        </PlanRestrictedFeatureWrapper>
    );

    const renderEditButton = (onClick: () => void) => (
        <PermissionedActionButton
            actionType={RunwayUserAction.UAUpdateAppSettings}
            type="link"
            icon={<EditOutlined />}
            disabled={isPaywalled}
            onClick={onClick}
        >
            Edit
        </PermissionedActionButton>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <Descriptions size="middle" layout="horizontal" bordered column={1} labelStyle={{ width: 300 }}>
                <Descriptions.Item label={renderLabel('Signals')}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        {savedEnabledSources.length > 0 ?
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {savedEnabledSources.map((source) => (
                                    <li key={source}>{displayTextForIssueSourceType(source)}</li>
                                ))}
                            </ul>
                        :   <Text type="secondary">No signals enabled</Text>}
                        {renderEditButton(openSignalsModal)}
                    </div>
                </Descriptions.Item>
                <Descriptions.Item label={renderLabel('Merge ticket subtasks into a single issue')}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text>{savedMergeSubtasks ? 'Yes' : 'No'}</Text>
                        {renderEditButton(openSubtasksModal)}
                    </div>
                </Descriptions.Item>
                <Descriptions.Item label={renderLabel('Ticket filters')}>
                    <TicketFiltersSettings orgId={orgId} appId={appId} />
                </Descriptions.Item>
            </Descriptions>

            <Modal
                title="Edit signals"
                open={openModal === 'signals'}
                okText="Save"
                cancelButtonProps={{ disabled: isLoading }}
                okButtonProps={{ loading: isLoading }}
                onOk={onSaveSignals}
                onCancel={() => setOpenModal(null)}
            >
                <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 8 }}>
                    <Text>Select which signals Triage will use to detect issues</Text>
                    <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder="Select signals"
                        options={sourceTypeOptions}
                        value={selectedSources}
                        onChange={setSelectedSources}
                    />
                    <Text type="secondary">Runway will not save historical data for excluded signals.</Text>
                </Space>
            </Modal>

            <Modal
                title="Edit subtask handling"
                open={openModal === 'subtasks'}
                okText="Save"
                cancelButtonProps={{ disabled: isLoading }}
                okButtonProps={{ loading: isLoading }}
                onOk={onSaveSubtasks}
                onCancel={() => setOpenModal(null)}
            >
                <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 8 }}>
                    <Space>
                        <Switch checked={mergeSubtasks} onChange={setMergeSubtasks} />
                        <Text>Merge ticket subtasks into a single issue</Text>
                    </Space>
                    <Text type="secondary">
                        When enabled, subtasks are attached to their parent ticket&apos;s issue instead of creating
                        their own.
                    </Text>
                </Space>
            </Modal>
        </div>
    );
};

export default TriageSettings;
